/**
 * useChatQueries — TanStack Query hooks for Operations Chat.
 *
 * Channel + message reads share one query cache. useOpsChatPoller is the single
 * incremental poll (ChatNotificationContext); panels read the same cache.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getMe,
  getOrCreateChannel,
  getMessages,
  getUnreadSummary,
} from '../services/chatApi';
import { useQueryScope } from '../utils/queryScope';

const ROOT = 'ops-chat';

/** Shared poll interval for ops chat (notification badge + open panels). */
export const CHAT_POLL_INTERVAL_MS = 12_000;

function messagesKey(scope, channelId) {
  return [ROOT, scope, 'messages', channelId, { sinceId: null }];
}

function mergeMessageLists(prev, incoming) {
  const prevList = Array.isArray(prev) ? prev : [];
  const addList = Array.isArray(incoming) ? incoming : [];
  if (addList.length === 0) return prevList;
  const byId = new Map(prevList.map((m) => [m.id, m]));
  addList.forEach((m) => byId.set(m.id, m));
  return Array.from(byId.values()).sort((a, b) => (a.id || 0) - (b.id || 0));
}

function useScope() {
  return useQueryScope();
}

export function useChatMe({ enabled = true } = {}) {
  const scope = useScope();
  return useQuery({
    queryKey: [ROOT, scope, 'me'],
    queryFn: getMe,
    enabled,
    staleTime: 5 * 60_000,
    retry: 0,
  });
}

export function useChatChannel(projectId, { enabled = true } = {}) {
  const scope = useScope();
  return useQuery({
    queryKey: [ROOT, scope, 'channel', projectId],
    queryFn: () => getOrCreateChannel(projectId),
    enabled: enabled && !!projectId,
    staleTime: 60_000,
    retry: 0,
  });
}

/** Full message list (sinceId=null). Incremental updates merge into this cache. */
export function useChatMessages(channelId, { enabled = true } = {}) {
  const scope = useScope();
  return useQuery({
    queryKey: messagesKey(scope, channelId),
    queryFn: async () => {
      const list = await getMessages(channelId, {});
      return Array.isArray(list) ? list : [];
    },
    enabled: enabled && !!channelId,
    staleTime: 0,
    retry: 0,
  });
}

/**
 * Cuántos sin leer y último id — sin traer el historial.
 *
 * Es lo único que el provider de notificaciones necesitaba: de cada mensaje
 * leía `m.id` y `m.sender_id` y nada más. Coste constante: con 0 y con 600
 * mensajes, una petición y dos consultas.
 *
 * El dato viene etiquetado con `canal` porque la clave cambia al cambiar de
 * proyecto y, durante un render, el dato aún es el del canal anterior: quien lo
 * consuma tiene que poder distinguirlo.
 */
export function useChatUnread(channelId, sinceId, { enabled = true } = {}) {
  const scope = useScope();
  return useQuery({
    queryKey: [ROOT, scope, 'unread', channelId, sinceId ?? null],
    queryFn: async () => {
      const data = await getUnreadSummary(channelId, sinceId);
      return {
        canal: channelId,
        unread_count: data?.unread_count ?? 0,
        latest_id: data?.latest_id ?? null,
      };
    },
    enabled: enabled && !!channelId,
    staleTime: 0,
    // Único `retry` del módulo, y con motivo: de esta consulta sale la marca de
    // arranque del poll (`latest_id`). Antes, un 5xx pasajero se tragaba en
    // `chatApi` y devolvía `latest_id: null`, con lo que el poll arrancaba en 0
    // y **se traía el historial entero** — el mismo defecto que retiró la Fase
    // C, por la puerta de un fallo de red. Ahora el error se propaga, así que
    // sin reintento el poll simplemente no arrancaría en toda la sesión.
    retry: 1,
  });
}

/**
 * Observa la caché de mensajes SIN pedirla.
 *
 * `enabled: false` suscribe a la caché pero no dispara `queryFn`, así que aquí
 * sólo aparece lo que el poll incremental va dejando — o el historial que haya
 * pedido un panel abierto por su cuenta. El montaje no pide nada.
 */
export function useChatMessagesCache(channelId) {
  const scope = useScope();
  const { data } = useQuery({
    queryKey: messagesKey(scope, channelId),
    queryFn: async () => [],
    enabled: false,
  });
  return data;
}

/** Merge sinceId deltas into the full-message query cache (panel + notification sync). */
export function useChatMessageMerge() {
  const queryClient = useQueryClient();
  const scope = useScope();

  const mergeIncremental = useCallback(async (channelId, sinceId) => {
    if (!channelId) return sinceId ?? null;
    if (sinceId == null) {
      await queryClient.invalidateQueries({ queryKey: messagesKey(scope, channelId) });
      return null;
    }
    try {
      const list = await getMessages(channelId, { sinceId });
      const safeList = Array.isArray(list) ? list : [];
      if (safeList.length === 0) return sinceId;
      queryClient.setQueryData(messagesKey(scope, channelId), (prev) =>
        mergeMessageLists(prev, safeList),
      );
      return safeList[safeList.length - 1]?.id ?? sinceId;
    } catch (e) {
      console.warn('Chat load messages failed:', e);
      return sinceId;
    }
  }, [queryClient, scope]);

  return { mergeIncremental };
}

/**
 * Background incremental poll — merges sinceId deltas into the messages cache.
 * Mount once per active project (ChatNotificationProvider); panels reuse cache.
 *
 * ─── POR QUÉ ESTE POLL NO ERA INCREMENTAL ────────────────────────────────────
 *
 * Medido (`chatNotificationMountCost.test.js`): 5 tics de 12 s → 5 peticiones,
 * las 5 **sin `since_id`**, o sea 5 historiales completos. La cadena era:
 *
 *   - `refreshSinceId` sólo corría al montar, con la caché todavía vacía porque
 *     la petición del historial seguía en vuelo → la marca quedaba en `null`;
 *   - con marca `null`, `mergeIncremental` invalidaba la consulta del historial
 *     completo y devolvía `null`;
 *   - y el tic sólo guardaba la marca `if (nextId != null)`.
 *
 * La marca no salía de `null` jamás. El coste no era «por montaje»: era cada 12
 * segundos, por usuario y por proyecto, mientras la pestaña estuviera abierta.
 *
 * Ahora la marca de arranque la trae el resumen (`latest_id`), y **sin marca no
 * se pregunta**: preguntar sin `since_id` es pedir el historial entero, que es
 * justo lo que se retira.
 *
 * @param {{canal: number, desde: number|null}|null} arranque marca inicial, ya
 *   etiquetada con su canal — durante un cambio de proyecto el resumen todavía
 *   es el del canal anterior, y usarlo pediría desde un id de otro canal.
 */
export function useOpsChatPoller(channelId, { enabled = true, arranque = null } = {}) {
  const { mergeIncremental } = useChatMessageMerge();
  const sinceIdRef = useRef(null);

  // Al cambiar de canal la marca vieja no vale para nada.
  useEffect(() => {
    sinceIdRef.current = null;
  }, [channelId]);

  useEffect(() => {
    if (!arranque || arranque.canal !== channelId) return;
    if (sinceIdRef.current != null) return;
    // Canal vacío (`latest_id` nulo) → 0: «todo lo que haya a partir de ahora».
    sinceIdRef.current = arranque.desde ?? 0;
  }, [arranque, channelId]);

  useEffect(() => {
    if (!enabled || !channelId) return undefined;
    const tick = () => {
      if (sinceIdRef.current == null) return;
      mergeIncremental(channelId, sinceIdRef.current)
        .then((nextId) => {
          if (nextId != null) sinceIdRef.current = nextId;
        })
        .catch(() => {});
    };
    const id = setInterval(tick, CHAT_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [channelId, enabled, mergeIncremental]);
}

export function useChatInvalidator() {
  const queryClient = useQueryClient();
  const scope = useScope();

  return useMemo(() => {
    const root = [ROOT, scope];
    const inv = (key) => queryClient.invalidateQueries({ queryKey: key });

    return {
      channel: (projectId) => inv([...root, 'channel', projectId]),
      messages: (channelId) => inv([...root, 'messages', channelId]),
      me: () => inv([...root, 'me']),
      everything: () => inv(root),
    };
  }, [queryClient, scope]);
}
